import { Modal } from "react-bootstrap";
import { EditableField } from "../../utils/editable_field";
import { refetch, useApiMutation } from "../../utils/api";
import { ReorderableList } from "../../utils/reorderable_list";
import { useState, useEffect } from "react";

const useDaySummaryActions = (tripId, dayDate) => {
  const { post } = useApiMutation();

  const setDaySummary = (value) =>
    post(`/api/user/set_day_summary/${tripId}`, {
      date: dayDate,
      value,
    }).then(() => refetch("trip"));

  return { setDaySummary };
};

const useActivityApi = (tripId, dayDate) => {
  const { post } = useApiMutation();

  const addActivity = (name, type) =>
    post(`/api/user/add_activity/${tripId}`, {
      activity: { name, date: dayDate, type },
    }).then(() => refetch("trip"));

  const updateActivity = (id, data) =>
    post(`/api/user/update_activity/${id}`, {
      activity: data,
    }).then(() => refetch("trip"));

  const deleteActivity = (id) =>
    post(`/api/user/delete_activity/${id}`, {
      activity: { id },
    }).then(() => refetch("trip"));

  const reorderActivities = (activities) =>
    post(`/api/user/reorder_activities/${tripId}`, {
      date: dayDate,
      activities_in_order: activities.map(({ id }) => id),
    }).then(() => refetch("trip"));

  return { addActivity, updateActivity, deleteActivity, reorderActivities };
};

const config = {
  transport: {
    fly: { icon: "fas fa-plane", label: "Fly" },
    fly_out: { icon: "fas fa-plane-departure", label: "Fly out" },
    fly_in: { icon: "fas fa-plane-arrival", label: "Fly in" },
    car: { icon: "fas fa-car-side", label: "Car" },
    bus: { icon: "fas fa-bus", label: "Bus" },
    train: { icon: "fas fa-train", label: "Train" },
    boat: { icon: "fas fa-ship", label: "Boat" },
  },
  accommodation: {
    hotel: { icon: "fas fa-hotel", label: "Hotel" },
    house: { icon: "fas fa-home", label: "House" },
    camping: { icon: "fas fa-campground", label: "Camping" },
  },
  weather: {
    sun: { icon: "bi bi-sun-fill", label: "Sun" },
    cloud: { icon: "fas fa-cloud", label: "Cloud" },
    rain: { icon: "fas fa-umbrella", label: "Rain" },
    cold: { icon: "fas fa-snowflake", label: "Cold" },
  },
};

export const DayModal = ({ trip, day, close, setDay, days }) => {
  const { setDaySummary } = useDaySummaryActions(trip.id, day.date);
  const { addActivity, updateActivity, deleteActivity, reorderActivities } =
    useActivityApi(trip.id, day.date);

  const [localActivities, setLocalActivities] = useState(day.activities);

  useEffect(() => {
    setLocalActivities(day.activities);
  }, [day.activities]);

  const handleReorder = (newActivities) => {
    setLocalActivities(newActivities);
    reorderActivities(newActivities);
  };

  return (
    <Modal show onHide={close} size="lg">
      <Modal.Body>
        <button
          type="button"
          onClick={close}
          className="p-0 m-0 fs-32 gray-4 btn btn-link position-absolute top-0 end-0 pr-4"
          aria-label="Close"
        >
          <i className="bi bi-x mr-1" />
        </button>

        <div className="d-flex align-items-center justify-content-center">
          <div className="w-25px text-center">
            {day.index > 0 && (
              <i
                className="bi bi-caret-left-fill fs-20 pointer"
                onClick={() => setDay(day.index - 1)}
              />
            )}
          </div>
          <div className="fs-24 mx-5 text-center">{day.day_long}</div>
          <div className="w-25px text-center">
            {day.index < days.length - 1 && (
              <i
                className="bi bi-caret-right-fill fs-20 pointer"
                onClick={() => setDay(day.index + 1)}
              />
            )}
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-center mt-4">
          <EditableField
            value={day.summary}
            submit={setDaySummary}
            className="text-center fs-20"
            placeholder="Day Summary"
            mode="edit"
          />
        </div>

        <hr />

        <BasicItemsSection
          title="Weather"
          configSection={config.weather}
          activities={day.weathers}
          trip={trip}
          day={day}
        />

        <hr />

        <div>
          <div className="fs-18 underline mb-1">Activities</div>

          <ReorderableList
            items={localActivities}
            itemId={(activity) => activity.id}
            onReorder={handleReorder}
            dragHandle
            renderItem={(activity: any, dragHandle) => (
              <div className="my-0 d-flex align-items-center">
                <div
                  {...dragHandle?.attributes}
                  className="d-flex align-items-center justify-content-center bg-gray-e grab"
                  style={{ padding: "4px 0px" }}
                >
                  <i className="bi bi-three-dots-vertical fs-10 gray-4" />
                </div>
                <EditableField
                  value={activity.name}
                  submit={(value) => updateActivity(activity.id, { name: value })}
                  className="ml-1"
                  placeholder="Activity"
                  mode="edit"
                />
                <EditableField
                  value={activity.notes}
                  submit={(value) => updateActivity(activity.id, { notes: value })}
                  className="ml-1"
                  placeholder="Notes"
                  mode="edit"
                />
                <i
                  className="fas fa-times ml-1 red fs-12 pointer hover-opacity"
                  onClick={() => deleteActivity(activity.id)}
                />
              </div>
            )}
          />

          <EditableField
            value=""
            submit={(value) => addActivity(value, "activity")}
            className="fs-14 mt-2"
            placeholder="Add an activity"
            mode="new"
          />
        </div>

        <hr />

        <BasicItemsSection
          title="Transport"
          configSection={config.transport}
          activities={day.transports}
          trip={trip}
          day={day}
        />

        <hr />

        <BasicItemsSection
          title="Accommodations"
          configSection={config.accommodation}
          activities={day.accommodations}
          trip={trip}
          day={day}
        />
      </Modal.Body>
    </Modal>
  );
};

export const BasicItemsSection = ({
  title,
  configSection,
  activities,
  placeholder = "Notes",
  trip,
  day,
}) => {
  const { addActivity, updateActivity, deleteActivity } = useActivityApi(
    trip.id,
    day.date
  );

  return (
    <div className="pb-1">
      <div className="fs-18 underline">{title}</div>

      <div className="mt-2 mb-2 fs-13 gray d-flex align-items-center">
        Add:
        {Object.entries(configSection).map(([type, data]) => (
          <i
            key={type}
            className={`${(data as any).icon} ml-2 pointer`}
            title={(data as any).label}
            onClick={() => addActivity("", type)}
          />
        ))}
      </div>
      {activities.map((activity) => (
        <div key={activity.id} className="d-flex align-items-center">
          <i
            className={`${configSection[activity.type]?.icon || ""}`}
            title={activity.name}
          />
          <EditableField
            value={activity.name}
            submit={(value) => updateActivity(activity.id, { name: value })}
            className="ml-1"
            placeholder={placeholder}
            mode="edit"
          />
          <i
            className="fas fa-times ml-1 red fs-12 pointer hover-opacity"
            onClick={() => deleteActivity(activity.id)}
          />
        </div>
      ))}
    </div>
  );
};
